import { IUTag } from '@kurtosys/types/helpers/AnalyticsHelper/Tealium/IUTag';

// Teach Typescript that there can be a utag on the window
declare global {
	interface Window {
		utag?: IUTag;
		a?: any;
	}
}

/**
 * flattener_version = 1.4;
 */
export interface ITealiumOptions {
	/**
	 * Specify a prefix for data layer elements being sent to the utag_data object.
	 * Instead of utag_data.productID, it could be utag_data.dl_productID
	 * teal.prefix = "dl_";
	 * @title Prefix
	 */
	prefix?: string;

	/**
	 * In cases of a nested object, what should join the parent key and child key
	 * @title Nested Delimiter
	 */
	nestedDelimiter?: string;

	/**
	 * Keys to be removed from the new flattened key name
	 * For a flattened key, you have digitalData.page.pageInfo.pageName and you want digitalData.page.pi.pageName
	 *  teal.replace_keys = {
	 * 	"pageInfo":"pi"
	 *   };
	 *
	 *
	 * For a flattened key, you have digitalData.page.pageInfo.pageName and you want digitalData.page.pageName
	 *  teal.replace_keys = {
	 * 	"pageInfo":""
	 *   };
	 *
	 *
	 * For a flattened key, you have digitalData.page.pageInfo.pageName and you want digitalData.pageName
	 *  teal.replace_keys = {
	 * 	"page":"",
	 * 	"pageInfo":""
	 *   };
	 *
	 * @title Replace Keys
	 */
	replaceKeys?: {
		[key: string]: string;
	};

	/**
	 * Ignore keys in the data layer that start with the following text.
	 * Expecting an object of strings
	 * teal.ignore_keys = {
	 *  "user" : 1,
	 *  "util" : 1
	 *  };
	 * @title Ignore Keys
	 */
	ignoreKeys?: {
		[key: string]: string | RegExp;
	};
}

export class TealiumHelper {
	teamliumOptions: ITealiumOptions;

	// working props
	replaceKeysList?: string[];
	ignoreKeysList?: string[];
	replaceKeysRegex?: {
		startOfString?: RegExp;
		endOfString?: RegExp;
	};
	nestedDelimiterRegex?: RegExp;

	constructor(teamliumOptions?: ITealiumOptions) {
		this.teamliumOptions = {
			prefix: 'ksys.',
			nestedDelimiter: '.',
			replaceKeys: {},
			ignoreKeys: {},
			...teamliumOptions,
		};
	}

	ignoreKey = (key: string): number => {
		let shouldIgnoreKey = 0;
		// Build a new array to avoid running through Object.keys multiple times
		if (typeof this.ignoreKeysList === 'undefined') {
			this.ignoreKeysList = Object.keys(this.teamliumOptions.ignoreKeys);
			this.ignoreKeysList.forEach((name) => {
				// Store a copy of the regex in the object
				this.teamliumOptions.ignoreKeys[name] = new RegExp('^' + name);
				if (key.match(this.teamliumOptions.ignoreKeys[name])) {
					shouldIgnoreKey = 1;
				}
			});
		} else {
			// Loop through the ignore_keys object to see if we should ignore this key
			this.ignoreKeysList.forEach((name) => {
				if (key.match(this.teamliumOptions.ignoreKeys[name])) {
					shouldIgnoreKey = 1;
				}
			});
		}
		return shouldIgnoreKey;
	};

	getKeyName = (key: string): string => {
		// Create a new object to store regexs or use existing one
		this.replaceKeysRegex = this.replaceKeysRegex || {};
		// Build a new array to avoid running through Object.keys multiple times
		if (typeof this.replaceKeysList === 'undefined') {
			this.replaceKeysList = Object.keys(this.teamliumOptions.replaceKeys);
			// Make a start of string and end of string regex
			this.replaceKeysRegex.startOfString = new RegExp('^[' + this.teamliumOptions.nestedDelimiter + ']');
			this.replaceKeysRegex.endOfString = new RegExp('[' + this.teamliumOptions.nestedDelimiter + ']$');
			this.replaceKeysList.forEach((name) => {
				// Store a copy of the regex in the this.replaceKeysRegex object so that the regexs are only built once
				this.replaceKeysRegex[name] = new RegExp(
					'[' +
						this.teamliumOptions.nestedDelimiter +
						']?' +
						name +
						'[' +
						this.teamliumOptions.nestedDelimiter +
						']?',
					'g',
				);
				key = this.keyReplace(key, name, this.replaceKeysRegex[name]);
			});
		} else {
			// Loop through the replace_keys object to see what we should be replacing
			this.replaceKeysList.forEach((name) => {
				key = this.keyReplace(key, name, this.replaceKeysRegex[name]);
			});
		}
		return key;
	};

	keyReplace = (key: string, name: string, re: string): string => {
		// Check to see if we are replacing the key name with a new value or if we are removing the key altogether
		if (this.teamliumOptions.replaceKeys[name] === '') {
			// The key needs to be removed completely
			key = key.replace(re, this.teamliumOptions.nestedDelimiter);
			// Check to see if the key starts with the nested delimiter and if so, remove it
			if (key.indexOf(this.teamliumOptions.nestedDelimiter) === 0) {
				const cleanRegEx = new RegExp('^[' + this.teamliumOptions.nestedDelimiter + ']');
				key = key.replace(cleanRegEx, '');
			}
		} else {
			// Make a copy of the original key to see how we need to update the key name
			const origKey = key;
			// Replace the key name
			key = key.replace(
				re,
				this.teamliumOptions.nestedDelimiter +
					this.teamliumOptions.replaceKeys[name] +
					this.teamliumOptions.nestedDelimiter,
			);
			// Check for the start of the origKey to see of the nested delimiter is there
			if (!origKey.match(this.replaceKeysRegex.startOfString)) {
				// Remove the nested delimiter from the start of the string
				const cleanRegEx = new RegExp('^[' + this.teamliumOptions.nestedDelimiter + ']');
				key = key.replace(cleanRegEx, '');
			}
			// Check for the end of the origKey to see of the nested delimiter is there
			if (!origKey.match(this.replaceKeysRegex.endOfString)) {
				// Add the nested delimiter to the end of the string
				const cleanRegEx = new RegExp('[' + this.teamliumOptions.nestedDelimiter + ']$');
				key = key.replace(cleanRegEx, '');
			}
		}
		return key;
	};

	processDataObject = (obj: object, newObj: object, parentKey?: string, createArray?: number): void => {
		if (typeof parentKey === 'undefined') {
			// This object isn't nested in another object
			parentKey = '';
		} else {
			// Add the nested_delimiter to the parent key if the delimiter isn't already at the end
			this.nestedDelimiterRegex =
				this.nestedDelimiterRegex || new RegExp('[' + this.teamliumOptions.nestedDelimiter + ']$');
			if (!parentKey.match(this.nestedDelimiterRegex)) {
				parentKey += '' + this.teamliumOptions.nestedDelimiter;
			}
		}
		Object.keys(obj).forEach((key) => {
			const nestedKeyName = parentKey + key;
			// Format the new key name and take out any whitespace
			const newKeyName = this.getKeyName((this.teamliumOptions.prefix + parentKey + key).replace(/\s/g, ''));
			// Set the keyType to limit the number of typeof checks
			const keyType = this.typeOf(obj[key]);
			if (keyType !== 'undefined' && keyType != null) {
				if (keyType.match(/boolean|string|number|date/) && !this.ignoreKey(key)) {
					// If obj[key] is a date, convert to ISOString
					if (this.typeOf(obj[key]) === 'date') {
						obj[key] = obj[key].toISOString();
					}
					// Check to see if we need to create an array for this data point
					if (createArray) {
						// First check to see if this key exists
						if (this.typeOf(newObj[newKeyName]) !== 'array') {
							// Make the key an array
							newObj[newKeyName] = [];
						}
						newObj[newKeyName].push('' + obj[key]); // Force value to be a string
					} else {
						// If the value of the key is a boolean or a string or a number and
						// the key shouldn't be ignored add to the data layer
						newObj[newKeyName] = '' + obj[key]; // Force value to be a string
					}
				} else if (keyType === 'object' && !this.ignoreKey(key)) {
					// Process this piece of the data layer and merge it
					this.processDataObject(obj[key], newObj, nestedKeyName, createArray);
				} else if (keyType === 'array') {
					this.processDataArray(obj[key], newObj, nestedKeyName);
				}
			}
		});
	};

	processDataArray = (obj: any[], newObj: object, parentKey: string): void => {
		const objLength = obj.length;
		if (typeof parentKey === 'undefined') {
			// This object isn't nested in another object
			parentKey = '';
		} else if (objLength > 0 && this.typeOf(obj[0]).match(/boolean|string|number|date/)) {
			// This is a normal array that doesn't need a nested delimiter
		} else {
			// Add the nested_delimiter to the parent key
			parentKey += '' + this.teamliumOptions.nestedDelimiter;
		}
		// Format the new key name and take out any whitespace
		const newKeyName = this.getKeyName((this.teamliumOptions.prefix + parentKey).replace(/\s/g, ''));
		for (let n = 0; n < objLength; n++) {
			const keyType = this.typeOf(obj[n]);
			if (keyType.match(/boolean|string|number|date/)) {
				// If obj[n] is a date, convert to ISOString
				if (keyType === 'date') {
					obj[n] = obj[n].toISOString();
				}
				// First check to see if this key exists
				if (this.typeOf(newObj[newKeyName]) !== 'array') {
					// Make the key an array
					newObj[newKeyName] = [];
				}
				// If the value of the key is a boolean or a string or a number and
				// the key shouldn't be ignored add to the data layer
				newObj[newKeyName].push('' + obj[n]);
			} else if (keyType === 'object') {
				this.processDataObject(obj[n], newObj, newKeyName, 1);
			}
		}
	};

	typeOf = (event: unknown): string => {
		return {}.toString
			.call(event)
			.match(/\s([a-zA-Z]+)/)[1]
			.toLowerCase();
	};

	flattenObject = (obj: object, newObj?: object): object => {
		// Make sure object exists
		if (typeof obj === 'undefined') {
			return;
		}
		// Check to see if we want to flatten the same object and keep the reference
		let mergeObject = false;
		if (obj === newObj) {
			mergeObject = true;
			// Start off a clean copy of the object
			newObj = {};
		}
		// Make sure new object exists
		if (typeof newObj === 'undefined') {
			newObj = {};
		}
		// Check to see if this object is an array
		if (this.typeOf(obj) === 'array') {
			// Store a safe copy of this object in case we are processing the b object
			const tempArray = JSON.parse(JSON.stringify(obj));
			const tempArrayLength = tempArray.length;
			// Clean up the object
			obj = {};
			// Let's see if the obj and newObj are the same.  If so, going to assume we are using the b object
			if (obj === newObj) {
				// Let's ensure that we have the a object
				// if (typeof window.a === 'undefined') {
				// 	// eslint-disable-next-line @typescript-eslint/no-unused-vars
				// 	// const a = 'view';
				// }
				// Add the automatic utag data points
				window.utag.loader.RD(newObj, window.a);
			}
			for (let i = 0; i < tempArrayLength; i++) {
				this.processDataObject(tempArray[i], newObj);
			}
		} else {
			this.processDataObject(obj, newObj);
		}
		if (mergeObject) {
			// Need to delete everything out of obj and replace with newObj
			Object.keys(obj).forEach((key) => {
				delete obj[key];
			});
			// Now that we have a clean original object, add everything from newObj which allows the reference to be kept
			Object.keys(newObj).forEach((key) => {
				obj[key] = newObj[key];
			});
		}

		return newObj;
	};
}
